import { Close } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { getStripeForAccount, hostname, parseError } from "../../../utils";

interface TopupPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  description: string;
  popular?: boolean;
}

interface TokenTopupDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
  onSuccess?: () => void;
  currentTopupCredits?: number;
  isPaidUser?: boolean;
  mode?: string;
  onOpenSubscription?: () => void;
}

// Payment form component that uses Stripe Elements
const TopupPaymentForm = ({
  selectedPackage,
  onSuccess,
  onCancel,
  getToken,
  eventListener,
  isPaidUser,
  connectedAccount,
}: {
  selectedPackage: TopupPackage;
  onSuccess: () => void;
  onCancel: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
  isPaidUser: boolean;
  connectedAccount?: string;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      // Submit the payment element
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Confirm the payment
      const { error: confirmError, paymentIntent } =
        await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: "if_required",
        });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // Confirm with backend to add credits
        const token = await getToken();
        const response = await axios.post(
          `${hostname}/subscriptions/topup/confirm`,
          {
            paymentIntentId: paymentIntent.id,
            connectedAccount: connectedAccount,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        eventListener({
          type: "SET_SUCCESS",
          message: response.data.message || "Credits added successfully!",
        });

        onSuccess();
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Order Summary
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography>{selectedPackage.name}</Typography>
          <Typography fontWeight="bold">${selectedPackage.price}</Typography>
        </Box>
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 1, mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography fontWeight="bold">Total</Typography>
            <Typography fontWeight="bold">${selectedPackage.price}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {selectedPackage.credits.toLocaleString()} credits (never expire)
          </Typography>
        </Box>
      </Paper>

      {!isPaidUser && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          An active subscription is required to use top-up credits. You can
          purchase now and subscribe later.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Method
        </Typography>
        <PaymentElement
          options={{
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
            wallets: {
              applePay: "auto",
              googlePay: "auto",
            },
          }}
        />
      </Paper>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button onClick={onCancel} disabled={processing}>
          Back
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={processing}
          disabled={!stripe || !elements}
        >
          Confirm ${selectedPackage.price}
        </LoadingButton>
      </Box>
    </Box>
  );
};

const TokenTopupDialog: React.FC<TokenTopupDialogProps> = ({
  open,
  onClose,
  getToken,
  eventListener,
  onSuccess,
  currentTopupCredits = 0,
  isPaidUser = false,
  mode = "light",
  onOpenSubscription,
}) => {
  const [packages, setPackages] = useState<TopupPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<TopupPackage | null>(
    null
  );
  const [step, setStep] = useState<"select" | "payment">("select");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [connectedAccount, setConnectedAccount] = useState<string | undefined>(
    undefined
  );
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [userIsPaid, setUserIsPaid] = useState(isPaidUser);
  const [topupCredits, setTopupCredits] = useState(currentTopupCredits);

  // Get the appropriate Stripe instance (platform or connected account for direct charges)
  const stripeInstance = useMemo(() => {
    return getStripeForAccount(connectedAccount);
  }, [connectedAccount]);

  useEffect(() => {
    if (open) {
      loadPackages();
      setStep("select");
      setSelectedPackage(null);
      setClientSecret(null);
      setConnectedAccount(undefined);
    }
  }, [open]);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/subscriptions/topup/packages`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPackages(response.data.packages);
      setUserIsPaid(response.data.isPaidUser);
      setTopupCredits(response.data.currentTopupCredits || 0);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelect = (pkg: TopupPackage) => {
    setSelectedPackage(pkg);
  };

  const handleBuyNow = async () => {
    if (!selectedPackage) return;

    setCreatingPayment(true);

    try {
      const token = await getToken();

      const response = await axios.post(
        `${hostname}/subscriptions/topup/create`,
        {
          packageId: selectedPackage.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setClientSecret(response.data.clientSecret);
      setConnectedAccount(response.data.connectedAccount);
      setUserIsPaid(response.data.isPaidUser);
      setStep("payment");
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setCreatingPayment(false);
    }
  };

  const handlePaymentSuccess = () => {
    onClose();
    if (onSuccess) {
      onSuccess();
    }
  };

  const handleBackToSelect = () => {
    setStep("select");
    setClientSecret(null);
    setConnectedAccount(undefined);
  };

  const handleUpgradeClick = () => {
    onClose();
    if (onOpenSubscription) {
      onOpenSubscription();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "500px",
          maxHeight: "90vh",
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ display: "flex", height: "100%", minHeight: 0 }}>
        {/* Left side - Image */}
        <Box
          sx={{
            width: "40%",
            minHeight: "500px",
            background:
              "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative elements */}
          <Box
            sx={{
              position: "absolute",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,215,0,0.3) 0%, transparent 70%)",
              top: "20%",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "150px",
              height: "150px",
              border: "2px solid rgba(255,255,255,0.1)",
              borderRadius: "20px",
              top: "25%",
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: "100px",
              height: "100px",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "15px",
              top: "30%",
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
            }}
          />
          {/* Stars */}
          {[...Array(20)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                width: Math.random() * 3 + 1 + "px",
                height: Math.random() * 3 + 1 + "px",
                backgroundColor: "rgba(255,255,255,0.5)",
                borderRadius: "50%",
                top: Math.random() * 100 + "%",
                left: Math.random() * 100 + "%",
              }}
            />
          ))}
          <Box sx={{ zIndex: 1, textAlign: "center", px: 3 }}>
            <Typography
              variant="h4"
              sx={{
                color: "white",
                fontWeight: "bold",
                textShadow: "0 0 20px rgba(255,215,0,0.5)",
                mb: 2,
              }}
            >
              Fuel Your Work
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.8)",
                lineHeight: 1.6,
              }}
            >
              Top-up credits that never expire. Always there when you need them.
            </Typography>
          </Box>
        </Box>

        {/* Right side - Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <DialogContent
            sx={{
              flex: 1,
              p: 3,
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 3,
              }}
            >
              <Typography variant="h5" fontWeight="bold">
                Credit Top-up
              </Typography>
              <IconButton onClick={onClose} size="small">
                <Close />
              </IconButton>
            </Box>

            {step === "select" && (
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Top-up credits never expire and are only used after all other
                  credits are depleted.
                  {!userIsPaid &&
                    " An active subscription is required to utilize these credits."}
                </Typography>

                {loading ? (
                  <Typography>Loading packages...</Typography>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {packages.map((pkg) => (
                      <Paper
                        key={pkg.id}
                        variant="outlined"
                        onClick={() => handlePackageSelect(pkg)}
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          position: "relative",
                          border: selectedPackage?.id === pkg.id ? 2 : 1,
                          borderColor:
                            selectedPackage?.id === pkg.id
                              ? "primary.main"
                              : "divider",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                          },
                        }}
                      >
                        {pkg.popular && (
                          <Chip
                            label="Popular"
                            size="small"
                            color="primary"
                            sx={{
                              position: "absolute",
                              top: -10,
                              right: 16,
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography variant="body1" fontWeight="500">
                              {pkg.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {pkg.description}
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="bold">
                            ${pkg.price}
                          </Typography>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 3 }}
                >
                  Alternatively,{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleUpgradeClick}
                    sx={{ cursor: "pointer" }}
                  >
                    upgrade your plan
                  </Link>{" "}
                  for better token value.
                </Typography>

                {!userIsPaid && (
                  <Alert severity="warning" sx={{ mt: 3 }}>
                    An active subscription is required to purchase top-up
                    credits.{" "}
                    <Link
                      component="button"
                      variant="body2"
                      onClick={handleUpgradeClick}
                      sx={{ cursor: "pointer", fontWeight: "bold" }}
                    >
                      Upgrade your plan
                    </Link>{" "}
                    to enable credit top-ups.
                  </Alert>
                )}

                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <LoadingButton
                    variant="contained"
                    size="large"
                    onClick={handleBuyNow}
                    loading={creatingPayment}
                    disabled={!selectedPackage || !userIsPaid}
                    sx={{ minWidth: "200px" }}
                  >
                    Buy Now
                  </LoadingButton>
                </Box>
              </Box>
            )}

            {step === "payment" && clientSecret && selectedPackage && (
              <Elements
                stripe={stripeInstance}
                options={{
                  clientSecret,
                  appearance: {
                    theme: mode === "dark" ? "night" : "stripe",
                  },
                }}
              >
                <TopupPaymentForm
                  selectedPackage={selectedPackage}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handleBackToSelect}
                  getToken={getToken}
                  eventListener={eventListener}
                  isPaidUser={userIsPaid}
                  connectedAccount={connectedAccount}
                />
              </Elements>
            )}
          </DialogContent>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TokenTopupDialog;
